// src/pages/Dashboard.js — redesigned 11 Oct 2026 (dashboard v1).
// Replaces the old KPI-cards + table page AND the separate Invoice Details page:
//   1. Stage tiles (tap one to filter the table below)
//   2. Instation / outstation split and "Out now"
//   3. Bills Marg synced (QR did not pick them up at billing)
//   4. Needs attention
//   5. The invoice table (every filter and column from both old pages)
// Data for sections 1-4 comes from GET /api/dashboard/summary. "Today" is the Indian day.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, getUser } from '../utils/api';
import { StatusBadge, Spinner, formatCurrency } from '../components/Shared';
import CashUpiBox from '../components/CashUpiBox';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import MissingInvoicesBox from '../components/MissingInvoicesBox';

// toISOString() is always UTC — adjust by the browser's own offset first so
// "Today" always means the actual local calendar date.
function localDateString(d) {
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
}
const today = () => localDateString(new Date());
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return localDateString(d); };

const PAGE_SIZE = 20;
const STAGES = [
  { value: '', label: 'All Stages' },
  { value: 'stage_1_erp',           label: 'ERP Raised' },
  { value: 'stage_2_stock_removal', label: 'Stock Removal' },
  { value: 'stage_3_checking',      label: 'Checking' },
  { value: 'stage_4_packing',       label: 'Ready for Delivery' },
  { value: 'stage_5_dispatched',    label: 'In Transit' },
  { value: 'stage_6_delivered',     label: 'Delivered' },
  { value: 'stage_7_returned_to_office', label: 'Person Returned' },
  { value: 'reattempt_pending',     label: 'Reattempt' },
  { value: 'cancelled',             label: 'Cancelled' },
  { value: 'complete',              label: 'Complete' },
];
const KINDS = [
  { value: '', label: 'All' },
  { value: 'instation', label: 'Instation' },
  { value: 'outstation', label: 'Outstation' },
];

// Same rule the sidebar uses: owner and team leader pass, everyone else needs the box ticked.
function userCan(key) {
  const u = getUser() || {};
  if (['owner', 'team_leader'].includes(u.role)) return true;
  return !!(u.permissions && u.permissions[key]);
}

const IST = 'Asia/Kolkata';
function fmtClock(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const sameDay = d.toLocaleDateString('en-CA', { timeZone: IST }) === new Date().toLocaleDateString('en-CA', { timeZone: IST });
  const t = d.toLocaleTimeString('en-IN', { timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: false });
  return sameDay ? t : `${d.toLocaleDateString('en-IN', { timeZone: IST, day: '2-digit', month: 'short' })} ${t}`;
}
function fmtStage(mins) {
  if (mins === null || mins === undefined) return '—';
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');

function Tile({ label, count, lines, warn, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={'flex-1 min-w-[150px] min-h-[128px] text-left rounded-lg border p-4 flex flex-col justify-between gap-1 transition-colors ' +
        (active
          ? 'bg-brand-700 border-brand-700 text-white'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-500 text-gray-800 dark:text-gray-100')}
    >
      <span className={'text-xs font-semibold uppercase tracking-wide ' + (active ? 'text-white/80' : 'text-gray-500 dark:text-gray-400')}>{label}</span>
      <span className="text-4xl font-bold tabular-nums leading-none">{count ?? '—'}</span>
      <span className={'text-xs whitespace-pre-line ' + (active ? 'text-white/80' : warn ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400')}>
        {lines}
      </span>
    </button>
  );
}

function Bar({ parts }) {
  const total = parts.reduce((a, p) => a + p.n, 0) || 1;
  return (
    <div className="flex h-3.5 rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
      {parts.map((p, i) => <div key={i} className={p.cls} style={{ width: `${(100 * p.n) / total}%` }} />)}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [summary, setSummary]     = useState(null);
  const [noAccess, setNoAccess]   = useState(false);
  const [loading, setLoading]     = useState(true);

  const [invoices, setInvoices]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loadingInv, setLoadingInv] = useState(false);
  const [filter, setFilter]       = useState('');
  const [statusFilter, setStatus] = useState('');
  const [kind, setKind]           = useState('');
  const [dateFrom, setDateFrom]   = useState(today());
  const [dateTo, setDateTo]       = useState(today());
  const [sortCol, setSortCol]     = useState('mng_number');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage]           = useState(1);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await get('/api/dashboard/summary'));
      setNoAccess(false);
    } catch (err) {
      if (/denied|permission/i.test(err.message || '')) setNoAccess(true);
      else console.error(err);
    } finally { setLoading(false); }
  }, []);

  const loadInvoices = useCallback(async (o) => {
    setLoadingInv(true);
    try {
      const offset = (o.page - 1) * PAGE_SIZE;
      let url = `/api/invoices?limit=${PAGE_SIZE}&offset=${offset}&sort=${o.sortCol}&order=${o.sortOrder}`;
      if (o.status) url += `&status=${encodeURIComponent(o.status)}`;
      if (o.kind)   url += `&delivery_type=${encodeURIComponent(o.kind)}`;
      // The backend searches both invoice number and party name with this one parameter.
      if (o.search) url += `&invoice_number=${encodeURIComponent(o.search)}`;
      if (o.from)   url += `&invoice_date_from=${encodeURIComponent(o.from)}`;
      if (o.to)     url += `&invoice_date_to=${encodeURIComponent(o.to)}`;
      const data = await get(url);
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoadingInv(false); }
  }, []);

  const current = () => ({ page, status: statusFilter, kind, search: filter, from: dateFrom, to: dateTo, sortCol, sortOrder });
  const lastUpdated = useAutoRefresh(loadSummary);
  useAutoRefresh(() => loadInvoices(current()));

  useEffect(() => { loadInvoices(current()); }, [page, statusFilter, kind, sortCol, sortOrder, dateFrom, dateTo]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); loadInvoices({ ...current(), page: 1 }); }, 400);
    return () => clearTimeout(t);
  }, [filter]); // eslint-disable-line

  // Settings page announces changes (invoice flow, overrides) — refresh when it does.
  useEffect(() => {
    const onChange = () => { loadSummary(); loadInvoices(current()); };
    window.addEventListener('dms-settings-changed', onChange);
    const onStorage = (e) => { if (e.key === 'dms-settings-changed') onChange(); };
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('dms-settings-changed', onChange); window.removeEventListener('storage', onStorage); };
  }, [page, statusFilter, kind, filter, dateFrom, dateTo, sortCol, sortOrder]); // eslint-disable-line

  function applyDates(from, to) {
    setDateFrom(from); setDateTo(to); setPage(1);
  }
  function pickStage(status, allDates) {
    setStatus(status); setPage(1);
    if (allDates) { setDateFrom(''); setDateTo(''); }
    else { setDateFrom(today()); setDateTo(today()); }
    document.getElementById('invoice-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function handleSort(col) {
    const order = col === sortCol ? (sortOrder === 'desc' ? 'asc' : 'desc') : (col === 'party_name' ? 'asc' : 'desc');
    setSortCol(col); setSortOrder(order); setPage(1);
  }
  const sortIcon = (col) => sortCol !== col
    ? <span className="text-gray-300 ml-1">↕</span>
    : <span className="text-brand-500 ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;

  const presets = [
    { label: 'Today',        from: today(),     to: today() },
    { label: 'Last 7 days',  from: daysAgo(6),  to: today() },
    { label: 'Last 30 days', from: daysAgo(29), to: today() },
    { label: 'All time',     from: '',          to: '' },
  ];
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const th = 'text-left px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase select-none';

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;

  const st = summary?.stages;
  const slowTxt = (n) => (n > 0 ? `${n} waiting over ${summary.thresholds.slow_hours} h` : 'none waiting long');
  const tiles = summary ? [
    { key: 'total', label: 'Total invoices today', count: summary.today.total,
      lines: `${summary.today.qr} scanned by QR\n${summary.today.marg} synced from Marg`,
      status: '', all: false },
    { key: 's1', label: 'ERP raised', count: st.s1.count, lines: slowTxt(st.s1.slow), warn: st.s1.slow > 0, status: 'stage_1_erp', all: true },
    { key: 's2', label: 'Stock removal', count: st.s2.count, lines: slowTxt(st.s2.slow), warn: st.s2.slow > 0, status: 'stage_2_stock_removal', all: true },
    { key: 's3', label: 'Checking', count: st.s3.count, lines: slowTxt(st.s3.slow), warn: st.s3.slow > 0, status: 'stage_3_checking', all: true },
    { key: 's4', label: 'Ready for delivery', count: st.s4.count, lines: slowTxt(st.s4.slow), warn: st.s4.slow > 0, status: 'stage_4_packing', all: true },
    { key: 's5', label: 'In transit', count: st.s5.count,
      lines: `${st.s5.older} from earlier days${st.s5.reattempt ? `\n${st.s5.reattempt} reattempt pending` : ''}`, warn: st.s5.older > 0, status: 'stage_5_dispatched', all: true },
    { key: 's6', label: 'Delivered', count: st.s6.count,
      lines: `${st.s6.older} older than a day\n${st.s7.count} with person returned`, warn: st.s6.older > 0, status: 'stage_6_delivered', all: true },
    { key: 'done', label: 'Complete today', count: st.complete_today, lines: 'vouchers back, closed', status: 'complete', all: true },
  ] : [];

  const att = summary?.attention;
  const attention = summary ? [
    { key: 'earlier', tone: 'red', level: 'Urgent', count: att.earlier_no_outcome.count,
      title: 'Taken out earlier, no outcome yet',
      detail: att.earlier_no_outcome.count
        ? att.earlier_no_outcome.people.map(p => `${p.person} ${p.n}`).join(', ') + '. Nobody has confirmed delivery or handover.'
        : 'Nothing outstanding from earlier days.',
      action: att.earlier_no_outcome.count ? { label: 'Show these bills', run: () => pickStage('stage_5_dispatched', true) } : null },
    { key: 'stuck', tone: 'amber', level: 'Watch', count: att.stuck.count,
      title: 'Waiting too long at a stage',
      detail: att.stuck.count ? `No movement for over ${summary.thresholds.slow_hours} hours, last 7 days. Oldest: ${att.stuck.oldest}.` : 'Nothing is held up.',
      action: null },
    { key: 'vouch', tone: 'amber', level: 'Watch', count: att.vouchers_old.instation + att.vouchers_old.outstation,
      title: 'Vouchers older than a day',
      detail: (att.vouchers_old.instation + att.vouchers_old.outstation)
        ? `Delivered, voucher not back. Outstation ${att.vouchers_old.outstation}, instation ${att.vouchers_old.instation}.`
        : 'All vouchers are up to date.',
      action: (att.vouchers_old.instation + att.vouchers_old.outstation) && userCan('vouchers')
        ? { label: 'Open Vouchers', run: () => navigate('/vouchers') } : null },
    { key: 'stale', tone: 'gray', level: 'Tidy', count: att.stale_deliveries,
      title: 'Open deliveries on closed bills',
      detail: att.stale_deliveries ? 'The bill is complete but its delivery row was never closed.' : 'No leftover open deliveries.',
      action: null },
  ] : [];
  const tone = {
    red:   'text-red-700 bg-red-50 dark:bg-red-950/40 dark:text-red-300',
    amber: 'text-amber-800 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300',
    gray:  'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
    ok:    'text-green-700 bg-green-50 dark:bg-green-950/40 dark:text-green-300',
  };

  const card = 'bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700';

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-700 dark:text-gray-100">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', timeZone: IST })}
            {lastUpdated && ` · updated ${lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · refreshes every 30 s`}
          </p>
        </div>
        <button onClick={() => { loadSummary(); loadInvoices(current()); }}
          className="px-4 py-2 min-h-[44px] text-sm font-semibold text-brand-700 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
          Refresh
        </button>
      </div>

      {noAccess && (
        <div className={card + ' p-5 text-sm text-gray-600 dark:text-gray-300'}>
          The summary tiles need the Dashboard permission, which hasn’t been granted to your account. You can still use the invoice table below.
        </div>
      )}

      {summary && (<>
        {/* 1. Stage tiles */}
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Today’s work</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">Stage tiles show every open bill right now · tap one to filter the table below</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {tiles.map(t => (
              <Tile key={t.key} label={t.label} count={t.count} lines={t.lines} warn={t.warn}
                active={statusFilter === t.status && (t.status !== '' || (dateFrom === today() && dateTo === today()))}
                onClick={() => pickStage(t.status, t.all)} />
            ))}
          </div>
        </section>

        {/* 2. Split + out now */}
        <div className="flex flex-wrap gap-6 items-stretch">
          <section className={card + ' p-5 flex-1 min-w-[320px] flex flex-col gap-4'}>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Instation and outstation</h2>
            {['instation', 'outstation'].map(k => {
              const s = summary.split[k];
              return (
                <div key={k} className="flex flex-col gap-2">
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="font-semibold">{cap(k)}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">{s.total} bills today</span>
                  </div>
                  <Bar parts={[{ n: s.done, cls: 'bg-green-600' }, { n: s.out, cls: 'bg-accent-500' }, { n: s.wait, cls: 'bg-gray-400' }]} />
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                    <span><b className="text-green-700 dark:text-green-400">{s.done}</b> delivered</span>
                    <span><b className="text-amber-700 dark:text-amber-400">{s.out}</b> out on the road</span>
                    <span><b>{s.wait}</b> not yet dispatched</span>
                    <span><b className="text-red-700 dark:text-red-400">{s.vouchers}</b> vouchers pending</span>
                  </div>
                </div>
              );
            })}
            {summary.split.unclassified > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{summary.split.unclassified} more today are still unclassified.</p>
            )}
          </section>

          <section className={card + ' p-5 flex-[2] min-w-[360px] flex flex-col gap-3'}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Out now</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">Bills with each person, and how many are confirmed in the app</span>
            </div>
            {summary.out_now.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4">Nobody is out with an unconfirmed bill.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-2 font-semibold">Person</th><th className="py-2 px-2 font-semibold">Round</th>
                    <th className="py-2 px-2 font-semibold">Out since</th><th className="py-2 px-2 font-semibold text-right">Bills</th>
                    <th className="py-2 pl-2 font-semibold w-1/3">Confirmed in app</th>
                  </tr></thead>
                  <tbody>
                    {summary.out_now.map((d, i) => (
                      <tr key={i} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="py-2.5 pr-2"><b className="font-semibold">{d.person}</b><div className="text-xs text-gray-500 dark:text-gray-400">{cap(d.delivery_type)}</div></td>
                        <td className="py-2.5 px-2 tabular-nums">{d.round_label ? `Round ${d.round_label}` : (d.round_number ? `Round ${d.round_number}` : '—')}</td>
                        <td className="py-2.5 px-2 tabular-nums">{fmtClock(d.out_since)}</td>
                        <td className="py-2.5 px-2 text-right font-semibold tabular-nums">{d.bills}</td>
                        <td className="py-2.5 pl-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2.5 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden">
                              <div className={d.confirmed === d.bills ? 'bg-green-600 h-full' : 'bg-accent-500 h-full'} style={{ width: `${(100 * d.confirmed) / d.bills}%` }} />
                            </div>
                            <span className="text-xs tabular-nums min-w-[44px] text-right">{d.confirmed}/{d.bills}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* 3. Synced from Marg */}
        <section className={card + ' p-5 flex flex-col gap-3'}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex flex-wrap items-baseline gap-3">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Synced from Marg</h2>
              <span className={'text-xs font-semibold px-2.5 py-1 rounded tabular-nums ' + tone.amber}>{summary.marg_only.total} TODAY</span>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">QR did not pick these up at billing. Marg created them.</span>
          </div>
          {summary.marg_only.rows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Every bill today came in through QR.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3 font-semibold">Invoice</th><th className="py-2 px-3 font-semibold">Party</th>
                  <th className="py-2 px-3 font-semibold">Marg synced at</th><th className="py-2 px-3 font-semibold">Stage now</th>
                  <th className="py-2 px-3 font-semibold">Type</th><th className="py-2 pl-3 font-semibold text-right">Value</th>
                </tr></thead>
                <tbody>
                  {summary.marg_only.rows.map(r => (
                    <tr key={r.invoice_id} onClick={() => navigate(`/invoices/${r.invoice_id}`)}
                      className="border-t border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-brand-50 dark:hover:bg-gray-700/40">
                      <td className="py-2.5 pr-3 font-semibold text-brand-700 dark:text-brand-500 tabular-nums">{r.invoice_number}</td>
                      <td className="py-2.5 px-3">{r.party_name}</td>
                      <td className="py-2.5 px-3 tabular-nums">{fmtClock(r.synced_at)}</td>
                      <td className="py-2.5 px-3"><StatusBadge status={r.status} /></td>
                      <td className="py-2.5 px-3">{cap(r.delivery_type)}</td>
                      <td className="py-2.5 pl-3 text-right tabular-nums">{formatCurrency(r.invoice_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {summary.marg_only.total > summary.marg_only.rows.length && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Showing the latest {summary.marg_only.rows.length} of {summary.marg_only.total}.</p>
          )}
        </section>

        <MissingInvoicesBox card={card} tone={tone} />

        <CashUpiBox data={summary.cash_upi} />

        {/* 4. Needs attention */}
        <section className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Needs attention</h2>
          <div className="flex flex-wrap gap-3">
            {attention.map(a => (
              <div key={a.key} className={card + ' flex-1 min-w-[250px] p-4 flex flex-col gap-2'}>
                <div className="flex justify-between items-center gap-2">
                  <span className={'text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded ' + (a.count === 0 ? tone.ok : tone[a.tone])}>
                    {a.count === 0 ? 'Clear' : a.level}
                  </span>
                  <span className="text-3xl font-bold tabular-nums">{a.count}</span>
                </div>
                <div className="font-semibold">{a.title}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{a.detail}</div>
                {a.action && (
                  <button type="button" onClick={a.action.run}
                    className="self-start min-h-[44px] text-sm font-semibold text-brand-700 dark:text-brand-500 underline">{a.action.label}</button>
                )}
              </div>
            ))}
          </div>
        </section>
      </>)}

      {/* 5. Invoice table */}
      <section id="invoice-table" className={card + ' overflow-clip'}>
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">All invoices</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">{total} invoice{total !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex flex-col gap-1 flex-[2] min-w-[220px]">
              <label htmlFor="dash-search" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Search</label>
              <input id="dash-search" type="text" placeholder="Invoice number or party name" value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
              <label htmlFor="dash-stage" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Stage</label>
              <select id="dash-stage" value={statusFilter} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="px-3 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500">
                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
              <label htmlFor="dash-from" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">From</label>
              <input id="dash-from" type="date" value={dateFrom} onChange={e => applyDates(e.target.value, dateTo)}
                className="px-3 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
              <label htmlFor="dash-to" className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">To</label>
              <input id="dash-to" type="date" value={dateTo} onChange={e => applyDates(dateFrom, e.target.value)}
                className="px-3 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 items-center">
            <div className="flex flex-wrap gap-2">
              {presets.map(p => {
                const on = dateFrom === p.from && dateTo === p.to;
                return (
                  <button key={p.label} type="button" onClick={() => applyDates(p.from, p.to)}
                    className={'px-3.5 min-h-[44px] text-sm rounded-lg border ' + (on
                      ? 'bg-brand-700 border-brand-700 text-white font-semibold'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700')}>
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              {KINDS.map(k => (
                <button key={k.value} type="button" onClick={() => { setKind(k.value); setPage(1); }}
                  className={'px-4 min-h-[44px] text-sm ' + (kind === k.value
                    ? 'bg-brand-700 text-white font-semibold'
                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700')}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto relative">
          {loadingInv && (
            <div className="absolute inset-0 bg-white/60 dark:bg-gray-800/60 flex items-center justify-center z-10"><Spinner /></div>
          )}
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th onClick={() => handleSort('mng_number')} className={th + ' cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'}>Invoice #{sortIcon('mng_number')}</th>
                <th onClick={() => handleSort('party_name')} className={th + ' cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'}>Party{sortIcon('party_name')}</th>
                <th className={th}>City</th>
                <th className={th}>Type</th>
                <th onClick={() => handleSort('status')} className={th + ' cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'}>Stage{sortIcon('status')}</th>
                <th className={th}>Handler</th>
                <th className={th}>Delivered</th>
                <th onClick={() => handleSort('value')} className={th + ' cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'}>Value{sortIcon('value')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-gray-400 dark:text-gray-500">No invoices found</td></tr>
              ) : invoices.map(inv => {
                const mins = inv.seconds_in_current_stage ? Math.floor(inv.seconds_in_current_stage / 60) : null;
                const recent = new Date(inv.invoice_date) >= new Date('2026-10-07T00:00:00+05:30');
                return (
                  <tr key={inv.invoice_id} onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                    className="border-t border-gray-200 dark:border-gray-700 hover:bg-brand-700/5 cursor-pointer transition-colors">
                    <td className="px-3 py-3 font-semibold text-brand-700 dark:text-brand-500 whitespace-nowrap">
                      {inv.invoice_number}
                      {inv.marg_seen_at ? (
                        <span title="Delivered by the Marg API" className="ml-1.5 text-green-600 dark:text-green-400">{'✓'}</span>
                      ) : (recent && (
                        <span title="Not yet delivered by the Marg API" className="ml-1.5 px-1 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">not in Marg</span>
                      ))}
                      {recent && (
                        <span title={inv.has_marg_items ? 'Marg line items received' : 'Marg line items not received yet'}
                          className={'ml-1 px-1 py-0.5 rounded text-[10px] font-semibold ' + (inv.has_marg_items ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500')}>
                          {inv.has_marg_items ? 'items ✓' : 'no items'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 font-medium">{inv.party_name}</td>
                    <td className="px-3 py-3 text-gray-500 dark:text-gray-400">{inv.city || '—'}</td>
                    <td className="px-3 py-3 text-gray-500 dark:text-gray-400">{cap(inv.delivery_type)}</td>
                    <td className="px-3 py-3"><StatusBadge status={inv.status} picked={inv.stock_removal_picked} /></td>
                    <td className="px-3 py-3 text-gray-500 dark:text-gray-400 text-xs">{inv.current_handler_name || inv.delivery_person_name || '—'}</td>
                    <td className="px-3 py-3 text-gray-500 dark:text-gray-400 text-xs tabular-nums whitespace-nowrap">{inv.delivered_at ? fmtDelivered(inv.delivered_at) : '—'}</td>
                    <td className="px-3 py-3 font-medium tabular-nums">{formatCurrency(inv.invoice_value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
              Page {page} of {totalPages} · Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-1 flex-wrap">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2.5 min-h-[44px] text-xs border rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">«</button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
                className="px-3 min-h-[44px] text-xs border rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...' ? (
                  <span key={`e${i}`} className="px-2 py-1 text-xs text-gray-400 dark:text-gray-500 self-center">…</span>
                ) : (
                  <button key={p} onClick={() => setPage(p)}
                    className={`px-3 min-h-[44px] text-xs border rounded ${page === p ? 'bg-brand-700 text-white border-brand-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                    {p}
                  </button>
                ))}
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages}
                className="px-3 min-h-[44px] text-xs border rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">Next ›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2.5 min-h-[44px] text-xs border rounded disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">»</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}


// Delivery confirmation time for the invoice table: time only if it was today (Indian day), else dd-mm and time.
function fmtDelivered(ts) {
  try {
    const d = new Date(ts);
    const tz = { timeZone: 'Asia/Kolkata' };
    const day = d.toLocaleDateString('en-CA', tz);
    const today = new Date().toLocaleDateString('en-CA', tz);
    const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, ...tz });
    return day === today ? time : day.slice(8, 10) + '-' + day.slice(5, 7) + ' ' + time;
  } catch (e) { return ''; }
}
